import React, { Context, createContext, PropsWithChildren, useCallback, useContext, useState } from 'react'
import { DEFAULT_PAGE_SIZE } from '../../constants'
import { callApi, TApiResponse } from '../api'
import { useAuthContext } from '../auth'
import { ICallsList, IRescheduleCall, TCallList, TCallsListActions } from '../types'
import { profileHeaderToken } from '../utils'

/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-empty-function */

const initialCallsListState: ICallsList = {
	upcoming: {
		list: [],
		page: 0,
		pageSize: DEFAULT_PAGE_SIZE,
		total: 0,
		loading: false,
	},
	past: {
		list: [],
		page: 0,
		pageSize: DEFAULT_PAGE_SIZE,
		total: 0,
		loading: false,
	},
	cancelled: {
		list: [],
		page: 0,
		pageSize: DEFAULT_PAGE_SIZE,
		total: 0,
		loading: false,
	},
	loading: false,
}

const CallsListContext: Context<{
	callsList: ICallsList
	actions: TCallsListActions
}> = createContext({
	callsList: initialCallsListState,
	actions: {
		getUpcoming: () => {},
		getPast: () => {},
		getCancelled: () => {},
		reschedule: (rescheduleCall: IRescheduleCall) => Promise.resolve(),
		cancel: (id: string, change_reason: string) => Promise.resolve(),
		resendInvite: (id: string) => Promise.resolve(),
	},
})

export const CallsListProvider = (props: PropsWithChildren<{}>) => {
	const { actions: authActions } = useAuthContext()
	const [upcomingList, setUpcomingList] = useState<TCallList[]>([])
	const [upcomingPage, setUpcomingPage] = useState<number>(0)
	const [upcomingPageSize, setUpcomingPageSize] = useState<number>(DEFAULT_PAGE_SIZE)
	const [upcomingTotal, setUpcomingTotal] = useState<number>(0)
	const [upcomingLoading, setUpcomingLoading] = useState<boolean>(false)
	const [loading, setLoading] = useState<boolean>(false)
	const [upcomingError, setUpcomingError] = useState<string | null>(null)
	const [pastList, setPastList] = useState<TCallList[]>([])
	const [pastPage, setPastPage] = useState<number>(0)
	const [pastPageSize, setPastPageSize] = useState<number>(DEFAULT_PAGE_SIZE)
	const [pastTotal, setPastTotal] = useState<number>(0)
	const [pastLoading, setPastLoading] = useState<boolean>(false)
	const [pastError, setPastError] = useState<string | null>(null)
	const [error, setProviderError] = useState<string | null>(null)
	const [cancelledList, setCancelledList] = useState<TCallList[]>([])
	const [cancelledPage, setCancelledPage] = useState<number>(0)
	const [cancelledPageSize, setCancelledPageSize] = useState<number>(DEFAULT_PAGE_SIZE)
	const [cancelledTotal, setCancelledTotal] = useState<number>(0)
	const [cancelledLoading, setCancelledLoading] = useState<boolean>(false)
	const [cancelledError, setCancelledError] = useState<string | null>(null)

	const fetchUpcoming = async (page?: number, pageSize?: number, token?: string) => {
		setUpcomingLoading(true)
		let paramObj = {
			query: {
				page: page === undefined ? upcomingPage : page,
				pageSize: pageSize === undefined ? upcomingPageSize : pageSize,
			},
		}

		if (token) paramObj = { ...paramObj, ...{ headers: profileHeaderToken(token) } }
		const callsListResponse: TApiResponse = await callApi(`/call/upcoming`, paramObj)

		const { data } = callsListResponse.payload

		if (callsListResponse.ok) {
			setUpcomingError(null)
			setUpcomingList(data.list)
			setUpcomingPage(data.page)
			setUpcomingPageSize(data.pageSize)
			setUpcomingTotal(data.total)
			setUpcomingLoading(false)
		} else {
			setUpcomingLoading(false)
			setUpcomingError(data.message || 'Cannot get Upcoming Calls!')
			if (callsListResponse.status === 401) {
				authActions.logout()
			}
		}
	}

	const fetchPast = async (page?: number, pageSize?: number, token?: string) => {
		setPastLoading(true)
		let paramObj = {
			query: {
				page: page === undefined ? upcomingPage : page,
				pageSize: pageSize === undefined ? upcomingPageSize : pageSize,
			},
		}

		if (token) paramObj = { ...paramObj, ...{ headers: profileHeaderToken(token) } }

		const callsListResponse: TApiResponse = await callApi(`/call/past`, paramObj)

		const { data } = callsListResponse.payload

		if (callsListResponse.ok) {
			setPastError(null)
			setPastList(data.list)
			setPastPage(data.page)
			setPastPageSize(data.pageSize)
			setPastTotal(data.total)
			setPastLoading(false)
		} else {
			setPastLoading(false)
			setPastError(data.message || 'Cannot get Past Calls!')
			if (callsListResponse.status === 401) {
				authActions.logout()
			}
		}
	}

	const fetchCancelled = async (page?: number, pageSize?: number, token?: string) => {
		setCancelledLoading(true)
		let paramObj = {
			query: {
				page: page === undefined ? cancelledPage : page,
				pageSize: pageSize === undefined ? cancelledPageSize : pageSize,
			},
		}

		if (token) paramObj = { ...paramObj, ...{ headers: profileHeaderToken(token) } }

		const callsListResponse: TApiResponse = await callApi(`/call/cancelled`, paramObj)

		const { data } = callsListResponse.payload

		if (callsListResponse.ok) {
			setCancelledError(null)
			setCancelledList(data.list)
			setCancelledPage(data.page)
			setCancelledPageSize(data.pageSize)
			setCancelledTotal(data.total)
			setCancelledLoading(false)
		} else {
			setCancelledLoading(false)
			setCancelledError(data.message || 'Cannot get Cancelled Calls!')
			if (callsListResponse.status === 401) {
				authActions.logout()
			}
		}
	}

	const fetchReschedule = async (rescheduleCall: IRescheduleCall) => {
		setLoading(true)
		const callResponse: TApiResponse = await callApi(`/call/${rescheduleCall.id}`, {
			method: 'put',
			body: {
				change_reason: rescheduleCall.change_reason,
				start_time: rescheduleCall.start_time,
				planned_duration: 50,
			},
		})

		const { data } = callResponse.payload

		if (callResponse.ok) {
			setProviderError(null)
			fetchUpcoming()
		} else {
			setPastError(data.message || 'Cannot Reschedule Call!')
			if (callResponse.status === 401) {
				authActions.logout()
			}
		}
		setLoading(false)
	}

	const fetchCancel = async (id: string, change_reason: string) => {
		setLoading(true)
		const callResponse: TApiResponse = await callApi(`/call/cancel/${id}`, {
			method: 'post',
			body: { change_reason },
		})

		const { data } = callResponse.payload

		if (callResponse.ok) {
			setProviderError(null)
			await Promise.all([fetchUpcoming(), fetchCancelled()])
		} else {
			setPastError(data.message || 'Cannot Cancel Call!')
			if (callResponse.status === 401) {
				authActions.logout()
			}
		}
		setLoading(false)
	}

	const fetchResendCall = async (id: string, token?: string) => {
		setLoading(true)
		const callResponse: TApiResponse = await callApi(`/call/resend-invite/${id}`, {
			method: 'post',
			...(token ? { headers: profileHeaderToken(token) } : {}),
		})

		const { data } = callResponse.payload

		if (callResponse.ok) {
			setProviderError(null)
			await Promise.all([fetchUpcoming(), fetchCancelled()])
		} else {
			setPastError(data.message || 'Cannot resend Call Invite!')
		}
		setLoading(false)
	}

	const getUpcoming = useCallback(fetchUpcoming, [authActions, upcomingPage, upcomingPageSize])
	const getPast = useCallback(fetchPast, [authActions, upcomingPage, upcomingPageSize])
	const getCancelled = useCallback(fetchCancelled, [authActions, upcomingPage, upcomingPageSize])
	const reschedule = useCallback(fetchReschedule, [authActions])
	const cancel = useCallback(fetchCancel, [authActions])
	const resendInvite = useCallback(fetchResendCall, [authActions])

	const actions: TCallsListActions = {
		getUpcoming,
		getPast,
		getCancelled,
		reschedule,
		cancel,
		resendInvite,
	}

	return (
		<CallsListContext.Provider
			value={{
				callsList: {
					upcoming: {
						list: upcomingList,
						page: upcomingPage,
						pageSize: upcomingPageSize,
						total: upcomingTotal,
						loading: upcomingLoading,
						error: upcomingError,
					},
					past: {
						list: pastList,
						page: pastPage,
						pageSize: pastPageSize,
						total: pastTotal,
						loading: pastLoading,
						error: pastError,
					},
					cancelled: {
						list: cancelledList,
						page: cancelledPage,
						pageSize: cancelledPageSize,
						total: cancelledTotal,
						loading: cancelledLoading,
						error: cancelledError,
					},
					loading,
					error,
				},
				actions,
			}}
		>
			{props.children}
		</CallsListContext.Provider>
	)
}

export const useCallsListContext: () => {
	callsList: ICallsList
	actions: TCallsListActions
} = () => {
	return useContext(CallsListContext)
}
